import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Rocket } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Pricing() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const fmt = (v) => (v % 1 === 0 ? v : v.toFixed(2));

  return (
    <div className="section-sand min-h-[80vh] py-14 px-4" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto text-center">
        <span className="inline-flex items-center gap-1 bg-canard/10 text-canard-dark rounded-full px-3 py-1 text-xs font-extrabold tracking-wide"><Rocket className="w-3.5 h-3.5" /> {t("pricingPage.badge")}</span>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-ink mt-4">{t("pricingPage.title")}</h1>
        <p className="text-muted2 font-body mt-3 max-w-2xl mx-auto">{t("pricingPage.subtitle")}</p>

        <div className="flex items-center justify-center gap-2 mt-8">
          <button data-testid="pricing-monthly" onClick={() => setBilling("monthly")} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${billing === "monthly" ? "bg-canard text-white" : "bg-white border border-border text-ink"}`}>{t("plans.monthly")}</button>
          <button data-testid="pricing-yearly" onClick={() => setBilling("yearly")} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${billing === "yearly" ? "bg-canard text-white" : "bg-white border border-border text-ink"}`}>{t("plans.yearly")} <span className="text-emeraude-dark bg-emeraude/15 rounded-full px-1.5 ml-1 text-xs">{t("plans.save15")}</span></button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {plans.map((p) => {
          const price = p.key === "free" ? 0 : (billing === "yearly" ? p.price_yearly : p.price_monthly);
          const highlight = p.key === "croissance";
          const pd = t(`planDefs.${p.key}`, { returnObjects: true, defaultValue: null });
          const name = pd?.name || p.name;
          const feats = Array.isArray(pd?.features) ? pd.features : p.features;
          return (
            <div key={p.key} data-testid={`pricing-card-${p.key}`} className={`relative rounded-2xl border p-6 flex flex-col bg-white text-left ${highlight ? "border-canard ring-2 ring-canard/25 shadow-cta" : "border-border"}`}>
              {highlight && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-canard text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full tracking-wide">POPULAIRE</span>}
              <p className="font-heading font-extrabold text-xl text-ink">{name}</p>
              <p className="mt-3">
                <span className="font-heading font-extrabold text-4xl text-canard">{p.key === "free" ? t("plans.free") : `${fmt(price)}€`}</span>
                {p.key !== "free" && <span className="text-xs text-muted2">{billing === "yearly" ? t("plans.perYear") : t("plans.perMonth")}</span>}
              </p>
              <ul className="mt-5 space-y-2 flex-1">
                {feats.map((f, i) => (<li key={i} className="flex gap-2 text-sm text-ink"><Check className="w-4 h-4 text-emeraude shrink-0 mt-0.5" /><span>{f}</span></li>))}
              </ul>
              <Button data-testid={`pricing-cta-${p.key}`} onClick={() => nav("/register?role=loueur")} className={`mt-6 rounded-full font-bold ${highlight ? "bg-canard hover:bg-canard-dark text-white" : "bg-sand hover:bg-pastel text-ink"}`}>
                {p.key === "free" ? t("pricingPage.ctaFree") : t("pricingPage.cta")}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
